import { z } from "zod";

const EmailTemplateRawSchema = z.object({
  slug: z.string(),
  title: z.string(),
  subject: z.string(),
  reply_to: z.string(),
  from_email: z.string(),
  from_name: z.string(),
  text: z.string(),
  html: z.string(),
  is_async: z.boolean(),
  attachments: z.array(z.string()),
  attributes: z.array(z.string()).default([]).optional() // TODO: Remove optional once BE mark it as mandatory
});

export const EmailTemplateSchema = z.object({
  slug: z.string(),
  title: z.string(),
  subject: z.string(),
  replyTo: z.string(),
  fromEmail: z.string(),
  fromName: z.string(),
  text: z.string(),
  html: z.string(),
  isAsync: z.boolean(),
  attachments: z.array(z.string()),
  attributes: z.array(z.string()).default([]).optional() // TODO: Remove optional once BE mark it as mandatory
});

export const EmailAttachmentSchema = z.object({
  title: z.string(),
  attachment: z.string() // url
});

export const BusinessUnitSchema = z.object({
  code: z.string(),
  name: z.string(),
  regions: z.array(z.string()).optional() // list of region codes
});

export const RegionSchema = z.object({
  code: z.string(),
  name: z.string()
});

const ExtraJSONOptionSchema = z
  .object({
    label: z.string(),
    value: z.string()
  })
  .catchall(z.any());

export const ExtraJSONSchema = z.object({
  tag: z.string(),
  description: z.string().optional(),
  data: z.union([
    z.record(z.array(ExtraJSONOptionSchema)),
    z.array(ExtraJSONOptionSchema)
  ])
});

const ProcessKindRawSchema = z.object({
  name: z.string(),
  tag: z.string(),
  is_active: z.boolean(),
  comments_allowed: z.boolean(),
  default_status: z.string(),
  available_statuses: z.array(z.string())
});

export const ProcessKindSchema = z.object({
  name: z.string(),
  tag: z.string(),
  isActive: z.boolean(),
  commentsAllowed: z.boolean(),
  defaultStatus: z.string(),
  availableStatuses: z.array(z.string())
});

// Is there a better way to do this?
export const ProcessStatusKinds = {
  1: "On Hold",
  2: "In Progress",
  3: "Completed",
  4: "Archive"
} as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ProcessStatusKindsSchema = z.enum([
  ProcessStatusKinds[1],
  ProcessStatusKinds[2],
  ProcessStatusKinds[3],
  ProcessStatusKinds[4]
]);

const ProcessStatusRawSchema = z.object({
  label: z.string(),
  kind: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  tag: z.string(),
  color_code: z.string().nullable().optional() // BE sends null if not present
});

const ObjectStatusRawSchema = z.object({
  name: z.string(),
  tag: z.string(),
  color_code: z.string()
});

export const ProcessStatusSchema = z.object({
  label: z.string(),
  kind: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  tag: z.string(),
  colorCode: z.string().optional()
});

const ObjectStatusSchema = z.object({
  name: z.string(),
  tag: z.string(),
  colorCode: z.string()
});

const ProcessPDFTemplateRawSchema = z.object({
  name: z.string(),
  html: z.string(),
  is_static: z.boolean(),
  tag: z.string()
});

export const ProcessPDFTemplateSchema = z.object({
  name: z.string(),
  html: z.string(),
  isStatic: z.boolean(),
  tag: z.string()
});

const AlertCategoryRawSchema = z.object({
  name: z.string(),
  is_visible: z.boolean(),
  can_subscribe: z.boolean(),
  help_text: z.string(),
  visibility_priority: z.number(),
  tag: z.string(),
  color_label: z.string().optional(),
  parent_category: z.string().optional()
});

export const AlertCategorySchema = z.object({
  name: z.string(),
  isVisible: z.boolean(),
  canSubscribe: z.boolean(),
  helpText: z.string(),
  visibilityPriority: z.number(),
  tag: z.string(),
  colorLabel: z.string().optional(),
  parentCategory: z.string().optional()
});

const CommentFlagOptionRawSchema = z.object({
  name: z.string().optional(),
  reason_code: z.array(z.string()).optional(),
  extra: z
    .object({
      color: z.string().optional(), // hex
      borderColor: z.string().optional(), // hex
      backgroundColor: z.string().optional() // hex
    })
    .optional(),
  tag: z.string(),
  post_to_activity_log: z.boolean(),
  user_groups: z.array(z.string()).optional()
});

export const CommentFlagOptionSchema = z.object({
  name: z.string().optional(),
  reasonCode: z.array(z.string()).optional(),
  color: z.string().optional(), // hex
  borderColor: z.string().optional(), // hex
  backgroundColor: z.string().optional(), // hex
  tag: z.string(),
  postToActivityLog: z.boolean(),
  userGroups: z.array(z.string()).optional()
});

export const UserGroupSchema = z.object({
  name: z.string(),
  permissions: z.array(z.string()).optional()
});

const ObjectWFRelationBehaviourEnum = z.enum([
  "initializer_object",
  "related_object",
  "object_creator"
]);

const ObjectWFRelationExtraSchema = z
  .object({
    object_reference: z.string().optional()
  })
  .catchall(z.any())
  .optional()
  .default({});

const ObjectTypeWorkflowKindRelationRawSchema = z.object({
  object_type: z.string(),
  workflow_kind: z.string(),
  relation: ObjectWFRelationBehaviourEnum,
  extra: ObjectWFRelationExtraSchema
});

export const ObjectTypeWorkflowKindRelationSchema = z.object({
  objectType: z.string(),
  workflowKind: z.string(),
  relation: ObjectWFRelationBehaviourEnum,
  extra: ObjectWFRelationExtraSchema
});

const IntegrationConfigSchema = z.object({
  name: z.string(),
  url: z.string(),
  method: z.string(),
  username: z.string(),
  password: z.string().optional(),
  requestBodyType: z.string(),
  requestBody: z.string(),
  advancedBodyRendering: z.boolean(),
  requestHeader: z.string(),
  requestThreshold: z.number(),
  responseListJsonPath: z.string(),
  responseListJmespath: z.string(),
  responseListUuidKey: z.string(),
  fileType: z.string(),
  fileJsonPath: z.string(),
  fileNameJsonPath: z.string(),
  childWorkflowRequestBody: z.string(),
  dataObjectRequestBody: z
    .union([z.array(z.any()), z.record(z.any())])
    .default([]),
  clientCertificatePrivate: z.string(),
  pollingInterval: z.number().optional(),
  failureEmails: z.array(z.any()).default([]),
  statusJsonPath: z.string(),
  apiType: z.string(),
  childWorkflowKind: z.string().optional(),
  dataObjectKind: z.string().optional(),
  integrationToken: z.string().optional(),
  nameMatchingParams: z.union([z.array(z.any()), z.record(z.any())]).optional(),
  extra: z.union([z.array(z.any()), z.record(z.any())]).optional()
});

const IntegrationTokenSchema = z.object({
  isStatic: z.boolean(),
  username: z.string(),
  password: z.string().optional(),
  tokenUrl: z.string(),
  tokenHeaders: z.string(),
  tokenBody: z.string(),
  tokenJsonPath: z.string(),
  freshTokenRequired: z.boolean(),
  token: z.string(),
  refreshInterval: z.number().optional(),
  clientCertificatePrivate: z.string(),
  tokenType: z.string()
});

const IntegrationConfigRawSchema = z.object({
  name: z.string(),
  url: z.string(),
  method: z.string(),
  username: z.string(),
  password: z.string().optional(),
  request_body_type: z.string(),
  request_body: z.string(),
  advanced_body_rendering: z.boolean(),
  request_header: z.string(),
  request_threshold: z.number(),
  response_list_json_path: z.string(),
  response_list_jmespath: z.string(),
  response_list_uuid_key: z.string(),
  file_type: z.string(),
  file_json_path: z.string(),
  file_name_json_path: z.string(),
  child_workflow_request_body: z.string(),
  data_object_request_body: z
    .union([z.array(z.any()), z.record(z.any())])
    .default([]),
  client_certificate_private: z.string(),
  polling_interval: z.number().optional(),
  failure_emails: z.array(z.any()).default([]),
  status_json_path: z.string(),
  api_type: z.string(),
  child_workflow_kind: z.string().optional(),
  data_object_kind: z.string().optional(),
  integration_token: z.string().optional(),
  name_matching_params: z
    .union([z.array(z.any()), z.record(z.any())])
    .optional(),
  extra: z.union([z.array(z.any()), z.record(z.any())]).optional()
});

const IntegrationTokenRawSchema = z.object({
  is_static: z.boolean(),
  username: z.string(),
  password: z.string().optional(),
  token_url: z.string(),
  token_headers: z.string(),
  token_body: z.string(),
  token_json_path: z.string(),
  fresh_token_required: z.boolean(),
  token: z.string(),
  refresh_interval: z.number().optional(),
  client_certificate_private: z.string(),
  token_type: z.string()
});

const FEExtraMetaDataSchema = z.object({
  // murmurhash hashing algo returns a number
  baseLiveAppSettingsHash: z.number().optional()
});

const AppSettingPermissionListSchema = z.record(z.record(z.record(z.string())));

const AppSettingsRawSchema = z.object({
  emailtemplate: z.array(EmailTemplateRawSchema),
  emailattachment: z.array(EmailAttachmentSchema),
  businessunit: z.array(BusinessUnitSchema),
  region: z.array(RegionSchema),
  extrajson: z.array(ExtraJSONSchema),
  workflowkind: z.array(ProcessKindRawSchema),
  workflowstatus: z.array(ProcessStatusRawSchema),
  objectstatus: z.array(ObjectStatusRawSchema).nullish(),
  workflowpdftemplate: z.array(ProcessPDFTemplateRawSchema),
  alertcategory: z.array(AlertCategoryRawSchema),
  commentflagoption: z.array(CommentFlagOptionRawSchema),
  group: z.array(UserGroupSchema),
  objecttypeworkflowkindrelation: z.array(
    ObjectTypeWorkflowKindRelationRawSchema
  ),
  integrationconfig: z.array(IntegrationConfigRawSchema),
  integrationtoken: z.array(IntegrationTokenRawSchema),
  file_paths: z.array(z.string()),
  frontend_extra_metadata: FEExtraMetaDataSchema.optional()
});

const TemplateAppSettingsRawSchema = AppSettingsRawSchema.pick({
  emailtemplate: true,
  businessunit: true,
  region: true,
  extrajson: true,
  workflowkind: true,
  workflowstatus: true,
  objectstatus: true,
  workflowpdftemplate: true,
  alertcategory: true,
  commentflagoption: true,
  group: true
}).partial();

const AppSettingsSchema = z.object({
  emailTemplate: z.array(EmailTemplateSchema),
  emailAttachment: z.array(EmailAttachmentSchema),
  businessUnit: z.array(BusinessUnitSchema),
  region: z.array(RegionSchema),
  extraJSON: z.array(ExtraJSONSchema),
  processKind: z.array(ProcessKindSchema),
  processStatus: z.array(ProcessStatusSchema),
  objectStatus: z.array(ObjectStatusSchema),
  processPDFTemplate: z.array(ProcessPDFTemplateSchema),
  alertCategory: z.array(AlertCategorySchema),
  commentFlagOption: z.array(CommentFlagOptionSchema),
  group: z.array(UserGroupSchema),
  objectTypeWorkflowKindRelation: z.array(ObjectTypeWorkflowKindRelationSchema),
  feExtraMetaData: FEExtraMetaDataSchema.optional(),
  integrationConfig: z.array(IntegrationConfigSchema),
  integrationToken: z.array(IntegrationTokenSchema)
});

const TemplateAppSettingsSchema = AppSettingsSchema.pick({
  emailTemplate: true,
  businessUnit: true,
  region: true,
  extraJSON: true,
  processKind: true,
  processStatus: true,
  objectStatus: true,
  processPDFTemplate: true,
  alertCategory: true,
  commentFlagOption: true,
  group: true
});

// Inferred types
export type EmailTemplateRaw = z.infer<typeof EmailTemplateRawSchema>;
export type EmailTemplate = z.infer<typeof EmailTemplateSchema>;
export type EmailAttachmentRaw = z.infer<typeof EmailAttachmentSchema>;
export type EmailAttachment = z.infer<typeof EmailAttachmentSchema>;
export type BusinessUnitRaw = z.infer<typeof BusinessUnitSchema>;
export type BusinessUnit = z.infer<typeof BusinessUnitSchema>;
export type RegionRaw = z.infer<typeof RegionSchema>;
export type Region = z.infer<typeof RegionSchema>;
export type ExtraJSONRaw = z.infer<typeof ExtraJSONSchema>;
export type ExtraJSON = z.infer<typeof ExtraJSONSchema>;
export type ProcessKindRaw = z.infer<typeof ProcessKindRawSchema>;
export type ProcessKind = z.infer<typeof ProcessKindSchema>;
export type ProcessStatusRaw = z.infer<typeof ProcessStatusRawSchema>;
export type ProcessStatus = z.infer<typeof ProcessStatusSchema>;
export type ObjectStatusRaw = z.infer<typeof ObjectStatusRawSchema>;
export type ObjectStatus = z.infer<typeof ObjectStatusSchema>;
export type ProcessPDFTemplateRaw = z.infer<typeof ProcessPDFTemplateRawSchema>;
export type ProcessPDFTemplate = z.infer<typeof ProcessPDFTemplateSchema>;
export type AlertCategoryRaw = z.infer<typeof AlertCategoryRawSchema>;
export type AlertCategory = z.infer<typeof AlertCategorySchema>;
export type CommentFlagOptionRaw = z.infer<typeof CommentFlagOptionRawSchema>;
export type CommentFlagOption = z.infer<typeof CommentFlagOptionSchema>;
export type UserGroup = z.infer<typeof UserGroupSchema>;
export type UserGroupRaw = z.infer<typeof UserGroupSchema>;
export type ObjectTypeWorkflowKindRelationRaw = z.infer<
  typeof ObjectTypeWorkflowKindRelationRawSchema
>;
export type ObjectTypeWorkflowKindRelation = z.infer<
  typeof ObjectTypeWorkflowKindRelationSchema
>;
export type IntegrationConfigRaw = z.infer<typeof IntegrationConfigRawSchema>;
export type IntegrationConfig = z.infer<typeof IntegrationConfigSchema>;
export type IntegrationTokenRaw = z.infer<typeof IntegrationTokenRawSchema>;
export type IntegrationToken = z.infer<typeof IntegrationTokenSchema>;
export type AppSettingsRaw = z.infer<typeof AppSettingsRawSchema>;
export type TemplateAppSettingsRaw = z.infer<
  typeof TemplateAppSettingsRawSchema
>;
export type AppSettings = z.infer<typeof AppSettingsSchema>;
export type TemplateAppSettings = z.infer<typeof TemplateAppSettingsSchema>;
export type AppSettingPermissionList = z.infer<
  typeof AppSettingPermissionListSchema
>;
export type ObjectWFRelationBehaviour = z.infer<
  typeof ObjectWFRelationBehaviourEnum
>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ObjectWFRelationBehaviourAsArray = ObjectWFRelationBehaviourEnum.options;
export const ObjectWFRelationBehaviourAsEnum =
  ObjectWFRelationBehaviourEnum.enum;

// types for JSONSchema for validation returned by BE
const JSONSchemaFieldTypesEnum = z.enum([
  "string",
  "boolean",
  "integer",
  "object",
  "array"
]);

const JSONSchemaRawSchema = z.object({
  type: z.array(JSONSchemaFieldTypesEnum),
  blank: z.boolean().optional(),
  maxLength: z.number().optional(),
  default: z.union([z.boolean(), z.string(), z.number()]).optional(),
  unique: z.boolean().optional()
});

const JSONSchemaSchema = z.object({
  type: z.union([JSONSchemaFieldTypesEnum, z.array(JSONSchemaFieldTypesEnum)]),
  isRequired: z.boolean(),
  maxLength: z.number().optional(),
  default: z.union([z.boolean(), z.string(), z.number()]).optional(),
  isBlankValueAllowed: z.boolean().optional(),
  unique: z.boolean().optional(), // also unique but may or may not be the primary key
  isUniqueIdentifier: z.boolean().optional() // primary key
});

export type JSONSchema = z.infer<typeof JSONSchemaSchema>;
export type JSONSchemaRaw = z.infer<typeof JSONSchemaRawSchema>;
type JSONSchemaFieldTypeEnum = z.infer<typeof JSONSchemaFieldTypesEnum>;
export const JSONSchemaFieldType = JSONSchemaFieldTypesEnum.enum;

export type AppSettingsJSONSchemaRaw = {
  definitions: {
    emailtemplate: {
      required: (keyof AppSettingsRaw["emailtemplate"][0])[];
      properties: Record<
        keyof AppSettingsRaw["emailtemplate"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["emailtemplate"][0];
      additionalProperties: boolean;
    };
    emailattachment: {
      required: (keyof AppSettingsRaw["emailattachment"][0])[];
      properties: Record<
        keyof AppSettingsRaw["emailattachment"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["emailattachment"][0];
      additionalProperties: boolean;
    };
    businessunit: {
      required: (keyof AppSettingsRaw["businessunit"][0])[];
      properties: Record<
        keyof AppSettingsRaw["businessunit"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["businessunit"][0];
      additionalProperties: boolean;
    };
    region: {
      required: (keyof AppSettingsRaw["region"][0])[];
      properties: Record<keyof AppSettingsRaw["region"][0], JSONSchemaRaw>;
      unique_identifier: keyof AppSettingsRaw["region"][0];
      additionalProperties: boolean;
    };
    extrajson: {
      required: (keyof AppSettingsRaw["extrajson"][0])[];
      properties: Record<keyof AppSettingsRaw["extrajson"][0], JSONSchemaRaw>;
      additionalProperties: boolean;
      unique_identifier: keyof AppSettingsRaw["extrajson"][0];
    };
    workflowkind: {
      required: (keyof AppSettingsRaw["workflowkind"][0])[];
      properties: Record<
        keyof AppSettingsRaw["workflowkind"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["workflowkind"][0];
      additionalProperties: boolean;
    };
    workflowstatus: {
      required: (keyof AppSettingsRaw["workflowstatus"][0])[];
      properties: Record<
        keyof AppSettingsRaw["workflowstatus"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["workflowstatus"][0];
      additionalProperties: boolean;
    };
    objectstatus: {
      required: (keyof ObjectStatusRaw)[];
      properties: Record<keyof ObjectStatusRaw, JSONSchemaRaw>;
      unique_identifier: keyof ObjectStatusRaw;
      additionalProperties: boolean;
    };
    workflowpdftemplate: {
      required: (keyof AppSettingsRaw["workflowpdftemplate"][0])[];
      properties: Record<
        keyof AppSettingsRaw["workflowpdftemplate"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["workflowpdftemplate"][0];
      additionalProperties: boolean;
    };
    alertcategory: {
      required: (keyof AppSettingsRaw["alertcategory"][0])[];
      properties: Record<
        keyof AppSettingsRaw["alertcategory"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["alertcategory"][0];
      additionalProperties: boolean;
    };
    commentflagoption: {
      required: (keyof AppSettingsRaw["commentflagoption"][0])[];
      properties: Record<
        keyof AppSettingsRaw["commentflagoption"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["commentflagoption"][0];
      additionalProperties: boolean;
    };
    group: {
      required: (keyof AppSettingsRaw["group"][0])[];
      properties: Record<keyof AppSettingsRaw["group"][0], JSONSchemaRaw>;
      unique_identifier: keyof AppSettingsRaw["group"][0];
      additionalProperties: boolean;
    };
    objecttypeworkflowkindrelation: {
      required: (keyof AppSettingsRaw["objecttypeworkflowkindrelation"][0])[];
      properties: Record<
        keyof AppSettingsRaw["objecttypeworkflowkindrelation"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["objecttypeworkflowkindrelation"][0];
      additionalProperties: boolean;
    };
    integrationconfig: {
      required: (keyof AppSettingsRaw["integrationconfig"][0])[];
      properties: Record<
        keyof AppSettingsRaw["integrationconfig"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["integrationconfig"][0];
      additionalProperties: boolean;
    };
    integrationtoken: {
      required: (keyof AppSettingsRaw["integrationtoken"][0])[];
      properties: Record<
        keyof AppSettingsRaw["integrationtoken"][0],
        JSONSchemaRaw
      >;
      unique_identifier: keyof AppSettingsRaw["integrationtoken"][0];
      additionalProperties: boolean;
    };
  };
  type: JSONSchemaFieldTypeEnum;
  required: string[];
  additionalProperties: boolean;
  // Not defining it since we won't be using it for validations
  properties: {};
};

export type AppSettingsJSONSchema = {
  emailTemplate: Record<keyof AppSettings["emailTemplate"][0], JSONSchema>;
  emailAttachment: Record<keyof AppSettings["emailAttachment"][0], JSONSchema>;
  businessUnit: Record<keyof AppSettings["businessUnit"][0], JSONSchema>;
  region: Record<keyof AppSettings["region"][0], JSONSchema>;
  extraJSON: Record<keyof AppSettings["extraJSON"][0], JSONSchema>;
  processKind: Record<keyof AppSettings["processKind"][0], JSONSchema>;
  processStatus: Record<keyof AppSettings["processStatus"][0], JSONSchema>;
  objectStatus: Record<keyof AppSettings["objectStatus"][0], JSONSchema>;
  processPDFTemplate: Record<
    keyof AppSettings["processPDFTemplate"][0],
    JSONSchema
  >;
  alertCategory: Record<keyof AppSettings["alertCategory"][0], JSONSchema>;
  commentFlagOption: Record<
    keyof AppSettings["commentFlagOption"][0],
    JSONSchema
  >;
  group: Record<keyof AppSettings["group"][0], JSONSchema>;
  objectTypeWorkflowKindRelation: Record<
    keyof AppSettings["objectTypeWorkflowKindRelation"][0],
    JSONSchema
  >;
  integrationConfig: Record<
    keyof AppSettings["integrationConfig"][0],
    JSONSchema
  >;
  integrationToken: Record<
    keyof AppSettings["integrationToken"][0],
    JSONSchema
  >;
};

// Helper function to create a validated schema based on JSON Schema rules
export const createValidatedSchema = <T extends z.ZodObject<any, any>>(props: {
  zodSchema: T;
  beJSONSchema: Record<string, JSONSchema>;
  existingValues: z.infer<T>[];
}): T => {
  const { zodSchema, beJSONSchema, existingValues = [] } = props;
  const shape = zodSchema.shape;

  const newShape = { ...shape };

  Object.entries(beJSONSchema).forEach(([key, validation]) => {
    // Skip if the field doesn't exist in base schema
    if (!(key in shape)) return;

    let fieldSchema = shape[key];

    // Handle string validations
    if (
      validation.type === JSONSchemaFieldType.string &&
      fieldSchema instanceof z.ZodString
    ) {
      if (validation.maxLength) {
        fieldSchema = fieldSchema.max(
          validation.maxLength,
          `Maximum length is ${validation.maxLength}`
        );
      }

      if (validation.isBlankValueAllowed === false) {
        fieldSchema = fieldSchema.min(1, "Field cannot be empty");
      }
    }

    // Handle required/optional
    if (!validation.isRequired) {
      fieldSchema = fieldSchema.optional();
    }

    // Handle default values
    if (validation.default !== undefined) {
      fieldSchema = fieldSchema.default(validation.default);
    }

    // Handle unique constraint
    if (validation.unique || validation.isUniqueIdentifier) {
      fieldSchema = fieldSchema.refine((val: unknown) => {
        if (val === undefined || val === null) return true;
        return !existingValues.some(item => item[key] === val);
      }, `${key} must be unique`);
    }

    newShape[key] = fieldSchema;
  });

  // Create new schema with the modified shape
  return z.object(newShape) as T;
};

export const templateAppSettingsRawValidator = (
  templateAppSettings: TemplateAppSettingsRaw
) => {
  return TemplateAppSettingsRawSchema.safeParse(templateAppSettings);
};
